import { ScenarioRunExactLimits } from '../../../api/api';
import { ExactLimitsBoard } from '../../../api/exactLimitsBoard';

/** Exact's limit per app per company per minute, when an answer did not say. */
export const MINUTELY_LIMIT = 60;

// How long a minute's count holds when Exact did not say when the minute resets: one minute at most.
const MINUTE_MS = 60_000;

/** The newest of what was read about Exact's day and minute, each with when it was read. */
export interface LimitsView {
    dailyLimit?: number;
    dailyRemaining?: number;
    dailyResetsAtUtc?: string;
    dailyReadAtUtc?: string;
    minutelyLimit?: number;
    minutelyRemaining?: number;
    minutelyResetsAtUtc?: string;
    minutelyReadAtUtc?: string;
    simulatedDailyUntilUtc?: string;
}

/** What a meter shows: the calls left of how many, and until when that holds. */
export interface Shown {
    remaining: number;
    /** Undefined when Exact never said how many calls the day has. */
    limit?: number;
    resetsAt?: number;
    simulated?: boolean;
}

const ms = (at?: string | null) => (at ? new Date(at).getTime() : undefined);
const value = <T,>(given?: T | null) => (given === null ? undefined : given);

/**
 * The newest of the environment's board and the run's own reading, per half: the board hears every caller, the run
 * only itself, and a run that has finished has no board worth asking.
 */
export const mergeLimits = (
    run?: ScenarioRunExactLimits,
    board?: ExactLimitsBoard
): LimitsView | undefined => {
    const runAt = run?.readAtUtc;
    const runDay = run && value(run.dailyRemaining) !== undefined;
    const runMinute = run && value(run.minutelyRemaining) !== undefined;
    const boardDay = board && value(board.dailyRemaining) !== undefined;
    const boardMinute = board && value(board.minutelyRemaining) !== undefined;
    const newer = (boardAt?: string | null) => (ms(boardAt) ?? 0) >= (ms(runAt) ?? 0);

    const day =
        boardDay && (!runDay || newer(board?.dailyReadAtUtc))
            ? {
                  dailyLimit: value(board?.dailyLimit),
                  dailyRemaining: value(board?.dailyRemaining),
                  dailyResetsAtUtc: value(board?.dailyResetsAtUtc),
                  dailyReadAtUtc: value(board?.dailyReadAtUtc),
              }
            : runDay
              ? {
                    dailyLimit: value(run?.dailyLimit),
                    dailyRemaining: value(run?.dailyRemaining),
                    dailyResetsAtUtc: value(run?.dailyResetsAtUtc),
                    dailyReadAtUtc: runAt,
                }
              : {};
    const minute =
        boardMinute && (!runMinute || newer(board?.minutelyReadAtUtc))
            ? {
                  minutelyLimit: value(board?.minutelyLimit),
                  minutelyRemaining: value(board?.minutelyRemaining),
                  minutelyResetsAtUtc: value(board?.minutelyResetsAtUtc),
                  minutelyReadAtUtc: value(board?.minutelyReadAtUtc),
              }
            : runMinute
              ? {
                    minutelyLimit: value(run?.minutelyLimit),
                    minutelyRemaining: value(run?.minutelyRemaining),
                    minutelyResetsAtUtc: value(run?.minutelyResetsAtUtc),
                    minutelyReadAtUtc: runAt,
                }
              : {};

    if (!runDay && !runMinute && !boardDay && !boardMinute && !board?.simulatedDailyUntilUtc) {
        return undefined;
    }

    return { ...day, ...minute, simulatedDailyUntilUtc: value(board?.simulatedDailyUntilUtc) };
};

/**
 * The minute as it stands at {@link now}. A count holds until its minute resets: the reset Exact sent with it, or,
 * when it sent none or an older one, a minute after it was read. After that the minute has started over.
 */
export const minuteShown = (view: LimitsView, now: number): Shown => {
    const limit = view.minutelyLimit ?? MINUTELY_LIMIT;
    const readAt = ms(view.minutelyReadAtUtc);
    const reset = ms(view.minutelyResetsAtUtc);
    const holdsUntil =
        readAt === undefined ? reset : reset !== undefined && reset > readAt ? reset : readAt + MINUTE_MS;

    if (view.minutelyRemaining === undefined || holdsUntil === undefined || holdsUntil <= now) {
        return { remaining: limit, limit };
    }

    return { remaining: view.minutelyRemaining, limit, resetsAt: holdsUntil };
};

/**
 * The day as it stands at {@link now}: none left while the connector acts as if the day is used up, a full day once
 * it has reset, otherwise what was read. Undefined when nothing was ever read about the day.
 */
export const dayShown = (view: LimitsView, now: number): Shown | undefined => {
    const limit = view.dailyLimit;
    const simulatedUntil = ms(view.simulatedDailyUntilUtc);

    if (simulatedUntil !== undefined && simulatedUntil > now) {
        return { remaining: 0, limit, resetsAt: simulatedUntil, simulated: true };
    }

    if (view.dailyRemaining === undefined) {
        return undefined;
    }

    const reset = ms(view.dailyResetsAtUtc);

    return reset !== undefined && reset <= now && limit !== undefined
        ? { remaining: limit, limit }
        : { remaining: view.dailyRemaining, limit, resetsAt: reset };
};
